import { Components, Data, List, Model, NeoModel } from "@singularsystems/neo-core";
import { Views } from "@singularsystems/neo-react";
import { NotificationDuration } from "../../../App/Models/NotificationDuration";
import { AppService } from "../../../App/Services/AppService";
import { DomainTypes, Types } from "../../DomainTypes";
import InterestRate from "../../Models/Rates/InterestRate";
import InterestRateType from "../../Models/Rates/InterestRateType";
import InterestRatePagedLookupCriteria from "../../Models/Rates/Lookups/InterestRatePagedLookupCriteria";
import AddChildInterestRateTypeCommand from "../../Models/Rates/Commands/AddChildInterestRateTypeCommand";
import DateCycle from "../../Models/Dividends/DateCycle";
import { DateCycleType } from "../../Models/Dividends/DateCycleType";
import DateCyclesVM from "./DateCyclesVM";
import { IDateCycleEditVM } from "../Investments/Components/DateCycleDropDown";
import InterestRateImportResult from "../../Models/Import/InterestRateImportResult";

@NeoModel
export default class InterestRateTypesVM extends Views.ViewModelBase implements IDateCycleEditVM {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications),
        private investmentSetupApiClient = AppService.get(DomainTypes.ApiClients.InvestmentSetup),
        private importApiClient = AppService.get(DomainTypes.ApiClients.Import),
        public dataCache = AppService.get(Types.Domain.Services.DataCache)) {

        super(taskRunner);

        this.fetchInterestRateTypes = this.fetchInterestRateTypes.bind(this);
        this.autoDispose(this.criteria.onAnyPropertyChanged(() => this.pageManager.refreshData()));
    }

    public interestRateTypeList = new List(InterestRateType);
    public criteria = new InterestRatePagedLookupCriteria();
    public interestRateTypeId: number | null = null;
    public editingRateType: InterestRateType | null = null;
    public addDayOffsetCommand: AddChildInterestRateTypeCommand | null = null;
    public importValidation: InterestRateImportResult | null = null;

    public get isDirty() {
        return this.editingRateType?.isDirty ?? false;
    }

    public pageManager = new Data.PageManager(this.criteria, InterestRate, this.investmentSetupApiClient.getInterestRateLookupsPaged, {
        pageSize: 10,
        initialTaskRunner: this.taskRunner,
        fetchInitial: true,
        allowSort: true,
        sortBy: "effectiveDate",
        sortAscending: false,
    });

    public fileManager = new Components.FileManager(this.importApiClient.getImportInterestRatesUrl(), {
        onFilesSelected: (files: any, fileManager: any) => this.onDocumentsSelected(files, fileManager),
        allowedExtensions: [".xlsx", ".csv"],
    });

    public async onDocumentsSelected(file: File[] | FileList, fileManager: Components.FileManager) {
        var result = await fileManager.uploadFiles(file, {
            additionalData: { interestRateTypeId: this.editingRateType!.interestRateTypeId },
        });

        this.importValidation = InterestRateImportResult.fromJSObject<InterestRateImportResult>(result);
        if (this.importValidation != null && !this.importValidation.hasErrors) {
            this.importValidation = null;
            this.pageManager.refreshData();
            this.notifications.addSuccess("Imported Successfully", "Imported interest rates successfully", NotificationDuration.Standard);
        }
    }

    public async fetchInterestRateTypes() {
        const data = await this.taskRunner.waitForData(this.investmentSetupApiClient.getInterestRateTypes(false));
        this.interestRateTypeList.set(data);
    }

    public async beginEditingInterestRate(interestRateType: InterestRateType) {
        this.editingRateType = interestRateType;
        this.pageManager.data.set([]);
        // onAnyPropertyChanged will cause the page manager to refresh when this changes:
        this.criteria.interestRateTypeId = interestRateType.interestRateTypeId;
    }

    public newInterestRate() {
        const interestRate = this.pageManager.data.addNew();
        interestRate.interestRateTypeId = this.criteria.interestRateTypeId!;
    }

    public async saveInterestRateTypes() {
        this.taskRunner.run(async () => {
            await this.saveRateTypes();
        });
    }

    public async saveInterestRates() {
        this.taskRunner.run(async () => {
            if (this.editingRateType) {
                if (!this.editingRateType.isChild && this.pageManager.data.readyToSave) {
                    var response = await this.investmentSetupApiClient.saveInterestRates(this.pageManager.data.toJSArray());
                    this.pageManager.data.update(response.data);
                    this.notifications.addSuccess("Save Successful", "Interest Rates saved successfully", NotificationDuration.Standard);
                }

                if (this.editingRateType?.readyToSave) {
                    await this.saveRateTypes();
                }
            }
        });
    }

    public async addInterestRateTypeChild(interestRateType: InterestRateType) {
        if (this.addDayOffsetCommand) {
            this.taskRunner.run(async () => {
                var response = await this.investmentSetupApiClient.addChildInterestRateTypeToParent(this.addDayOffsetCommand!.toJSObject());
                interestRateType.set(response.data);
                this.addDayOffsetCommand = null;
            });
        }
    }

    public dateCycleVM = this.registerViewModel(DateCyclesVM);

    public editDateCycle(dateCycleId: number | null, dateCycle: DateCycle | null, dateCycleType: DateCycleType) {

        if (dateCycleId === null) {
            dateCycle = new DateCycle();
            dateCycle.dateCycleName = "Custom";
            dateCycle.forSingleInvestment = false;
            dateCycle.dateCycleType = dateCycleType;
        }

        if (dateCycle) {
            this.dateCycleVM.editDateCycle(dateCycle, false);
        } else {
            this.dateCycleVM.beginEditDateCycle(dateCycleId!, false);
        }
    }

    public async saveDateCycle() {

        if (this.dateCycleVM.editingDateCycle && this.editingRateType) {
            if (this.dateCycleVM.editingDateCycle.isNew) {
                this.editingRateType.rateFrequency = this.dateCycleVM.editingDateCycle;
                this.editingRateType.rateFrequencyId = 0;
                this.editingRateType.rateFrequencyName = this.dateCycleVM.editingDateCycle.dateCycleName;
            } else {
                await this.dateCycleVM.saveDateCycle();
            }
        }
        this.dateCycleVM.editingDateCycle = null;
    }

    private async saveRateTypes() {
        var response = await this.investmentSetupApiClient.saveInterestRateTypes(this.interestRateTypeList.toJSArray());
        this.interestRateTypeList.update(response.data);
        this.dataCache.interestRateTypes.expire();
        this.notifications.addSuccess("Save Successful", "Interest Rate Types saved successfully", NotificationDuration.Standard);
    }
}